/**
 * stringHelper.js
 * Created by Michael Bray on 2/13/18.
 */

/**
 * @param {string} str a string to capitalize the first letter in
 * @returns {string} a string with first letter capitalized. For example:
 *
 *      capitalize('some string here');
 *      >> 'Some string here'
 *
 */
export function capitalize(str) {
    return `${str.charAt(0).toUpperCase()}${str.slice(1)}`;
}

// Certain minor words should be left lowercase unless
// they are the first or last words in the string
const _lowers = [
    'a',
    'an',
    'the',
    'and',
    'but',
    'or',
    'for',
    'nor',
    'as',
    'at',
    'by',
    'for',
    'from',
    'in',
    'into',
    'near',
    'of',
    'on',
    'onto',
    'to',
    'with',
];
const _lowerPatterns = [];
for (let i = 0; i < _lowers.length; i++) {
    _lowerPatterns.push(new RegExp(`\\s${_lowers[i]}\\s`, 'ig'));
}
_lowerPatterns.push(new RegExp(`\\'S`, 'ig'));

// Certain words such as initialisms or acronyms should be left uppercase
const _uppers = [
    'USS',
    'AK',
    'AL',
    'AR',
    'AZ',
    'CA',
    'CO',
    'CT',
    'DC',
    'DE',
    'FL',
    'GA',
    'HI',
    'IA',
    'ID',
    'IL',
    '\\(IN\\)',
    'KS',
    'KY',
    'LA',
    'MA',
    'MD',
    'ME',
    'MI',
    'MN',
    'MO',
    'MS',
    'MT',
    'NC',
    'ND',
    'NE',
    'NH',
    'NJ',
    'NM',
    'NV',
    'NY',
    'OH',
    'OK',
    'OR',
    'PA',
    'RI',
    'SC',
    'SD',
    'TN',
    'TX',
    'UT',
    'VA',
    'VT',
    'WA',
    'WI',
    'WV',
    'WY',
    'NWR',
    'ORV',
];
const _upperPatterns = [];
for (let i = 0; i < _uppers.length; i++) {
    if (_uppers[i].indexOf('(') >= 0) {
        _upperPatterns.push(new RegExp(`(^|\\s)${_uppers[i]}(\\s|$)`, 'ig'));
    }else{
        _upperPatterns.push(new RegExp(`(^|\\s|\\()${_uppers[i]}(\\s|\\)|$)`, 'ig'));
    }
}

/**
 * Capitalize string while trying to keep cases more or less correct
 */
export function toTitleCase(str) {
    if (str) {
        let s = str.replace(/\w*/g, (txt) => {
            return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
        });

        // Certain minor words should be left lowercase unless
        for (let i = 0; i < _lowerPatterns.length; i++) {
            s = s.replace(_lowerPatterns[i], (txt) => {
                return txt.toLowerCase();
            });
        }

        // Certain words such as initialisms or acronyms should be left uppercase
        for (let i = 0; i < _upperPatterns.length; i++) {
            s = s.replace(_upperPatterns[i], (txt) => {
                return txt.toUpperCase();
            });
        }
        return s;
    }

    return '';
}

/**
 * replaceAccents
 * -------------------
 * Helper function for sluggify - replaces accent char with non-accent char
 *
 * @param  {string} str [description]
 * @return {string} - string with no accents
 */
function replaceAccents(str) {
    const accents = 'ÀÁÂÃÄÅĀàáâãäåāÒÓÔÕÕÖØòóôõöøÈÉÊËèéêëðÇçÐÌÍÎÏìíîïÙÚÛÜùúûüÑñŠšŸÿýŽž';
    const accentReplacement =
        'AAAAAAAaaaaaaaOOOOOOOooooooEEEEeeeeeCcDIIIIiiiiUUUUuuuuNnSsYyyZz';
    const newStr = str.split('');
    newStr.forEach((letter, index) => {
        const i = accents.indexOf(letter);
        if (i !== -1) {
            newStr[index] = accentReplacement[i];
        }
    });
    return newStr.join('');
}

/**
 * sluggifyString
 * -------------------
 * Helper function for sluggify - removes punc and replaces spaces with hyphens
 *
 * @param  {string} str - string to be mofidied
 * @returns {string} - a lowercase string with no spaces
 */
function sluggifyString(str) {
    if (typeof str !== 'string') {
        // eslint-disable-next-line no-console
        console.error(
            'Invalid type passed to sluggifyString. Must be a string. Received:',
            str
        );
        return '';
    }
    const newStr = replaceAccents(str);

    return newStr
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/--+/g, '-') // Replace multiple - with single -
        .replace(/[^\w-]+/g, '') // Remove non-word chars with -
        .replace(/^-+/, '') // Trim - from start of text
        .replace(/-+$/, ''); // Trim - from end
}

/**
 * sluggify
 * -------------------
 * Turns a single string or an array of strings into a single lowercase string
 * with punctuation removed and spaces replaced by hyphens
 *
 * @param  {string || array} strs - a single string or an array of strings
 * @returns {string} - a lowercase string with no spaces or punctuation
 */
export function sluggify(strs) {
    // If param is a string, return the modified string
    if (typeof strs === 'string') {
        return sluggifyString(strs);
    }

    if (!Array.isArray(strs)) {
        // eslint-disable-next-line no-console
        console.error(
            'Invalid type passed to sluggify. Must be a string or an array of strings. Received: ',
            strs
        );
        return '';
    }

    // concat each string onto the slug variable with a hyphen between each item
    return strs.map((str) => sluggifyString(str)).join('-');
}

/**
 * pluralize
 * -------------------
 * Returns the `singular` or `plural` string depending on the length of the param
 *
 * @param  {string || array} strs - a number or array
 * @param  {string} singular - the singular string to return
 * @param  {string} plural - the plural string to return
 * @returns {string} - the singular or plural string based on the length of the first input
 */
export const pluralize = (pluralizeArg, singular, plural) => {
    if (typeof pluralizeArg === 'number') {
        return pluralizeArg === 1 ? singular : plural;
    } else if (pluralizeArg instanceof Array) {
        return pluralizeArg.length === 1 ? singular : plural;
    }
    return plural;
};
